import type { AGENT_STEPS } from "../../CustomHooks/customIndustryAgent.types";
import { AGENT_STEPS as STEPS } from "../../CustomHooks/customIndustryAgent.types";

type AgentStep = (typeof AGENT_STEPS)[number];

/** The two nodes that exist only to produce segments. The agent routes straight past both
 *  when the caller has no segment budget, so neither reports anything to wait for. */
const SEGMENT_ONLY_STEPS: readonly AgentStep[] = ["classify_domains", "create_segments"];

/** From here on the industry exists, so cancelling the run would throw it away while only
 *  the overview warm-up is left. */
export const hasPublishedIndustry = (completedSteps: number): boolean =>
    completedSteps > STEPS.indexOf("publish_assets");

/** `0` is the one budget that skips work: no free segments, or no segments module at all. */
export const isStepSkipped = (step: AgentStep, maxSegments: number): boolean =>
    maxSegments === 0 && SEGMENT_ONLY_STEPS.includes(step);

export const runStepCount = (maxSegments: number): number =>
    STEPS.filter((step) => !isStepSkipped(step, maxSegments)).length;

/**
 * Which step is running now.
 *
 * `completedSteps` counts positions in `AGENT_STEPS`, because that is what the stream's node
 * names map onto — so on a skipping run it lands on a step nobody is working on, and the real
 * one is the next position the agent did not skip.
 */
export const currentStepIndex = (completedSteps: number, maxSegments: number): number => {
    for (let index = completedSteps; index < STEPS.length; index++) {
        if (!isStepSkipped(STEPS[index], maxSegments)) {
            return index;
        }
    }

    return STEPS.length - 1;
};

/** Where the current step sits once the skipped ones are not counted, 1-based. */
export const currentStepNumber = (completedSteps: number, maxSegments: number): number => {
    const current = currentStepIndex(completedSteps, maxSegments);

    return STEPS.slice(0, current + 1).filter((step) => !isStepSkipped(step, maxSegments)).length;
};
